import { useEffect, type RefObject } from "react";

const HUBSPOT_PORTAL_ID = "143197515";
const HUBSPOT_REGION = "eu1";
// Use legacy v2 embed which universally supports all forms (incl. older ones
// that don't render via the new hs-form-frame loader and show as a white bar).
const HUBSPOT_SCRIPT_SRC = `https://js-${HUBSPOT_REGION}.hsforms.net/forms/v2.js`;

let scriptLoadingPromise: Promise<void> | null = null;

function loadHubSpotScript(): Promise<void> {
  if (scriptLoadingPromise) return scriptLoadingPromise;

  scriptLoadingPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      `script[src="${HUBSPOT_SCRIPT_SRC}"]`,
    ) as HTMLScriptElement | null;
    if (existing) {
      if ((window as unknown as { hbspt?: unknown }).hbspt) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = HUBSPOT_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.type = "text/javascript";
    script.charset = "utf-8";
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadingPromise = null;
      reject(new Error("HubSpot embed failed"));
    };
    document.head.appendChild(script);
  });

  return scriptLoadingPromise;
}

export function useHubSpotFormEmbed(
  containerRef: RefObject<HTMLDivElement | null>,
  formId: string | undefined,
  customStyles = "",
) {
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !formId) return;

    el.innerHTML = "";

    if (customStyles) {
      const style = document.createElement("style");
      style.textContent = customStyles;
      el.appendChild(style);
    }

    const target = document.createElement("div");
    target.id = `hs-form-${formId}-${Math.random().toString(36).slice(2, 8)}`;
    el.appendChild(target);

    let cancelled = false;

    loadHubSpotScript()
      .then(() => {
        if (cancelled) return;
        const w = window as unknown as {
          hbspt?: {
            forms?: { create?: (opts: Record<string, unknown>) => void };
          };
        };
        if (w.hbspt?.forms?.create) {
          try {
            w.hbspt.forms.create({
              region: HUBSPOT_REGION,
              portalId: HUBSPOT_PORTAL_ID,
              formId,
              target: `#${target.id}`,
            });
          } catch (e) {
            console.warn("hbspt.forms.create failed", e);
          }
        }
      })
      .catch((err) => {
        console.error("HubSpot form failed to load", { formId, err });
      });

    return () => {
      cancelled = true;
      el.innerHTML = "";
    };
  }, [containerRef, formId, customStyles]);
}

// New HubSpot embed loader (forms built in the new form builder).
// These forms render via <div class="hs-form-frame" data-form-id="..."> and
// do NOT work with the legacy v2.js (hbspt.forms.create) loader.
const HUBSPOT_FRAME_SCRIPT_SRC = `https://js-${HUBSPOT_REGION}.hsforms.net/forms/embed/${HUBSPOT_PORTAL_ID}.js`;

let frameScriptPromise: Promise<void> | null = null;

function loadHubSpotFrameScript(): Promise<void> {
  if (frameScriptPromise) return frameScriptPromise;
  frameScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      `script[src="${HUBSPOT_FRAME_SCRIPT_SRC}"]`,
    ) as HTMLScriptElement | null;
    if (existing) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = HUBSPOT_FRAME_SCRIPT_SRC;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      frameScriptPromise = null;
      reject(new Error("HubSpot embed loader failed"));
    };
    document.body.appendChild(s);
  });
  return frameScriptPromise;
}

export function useHubSpotFrameEmbed(
  containerRef: RefObject<HTMLDivElement | null>,
  formId: string | undefined,
  customStyles = "",
) {
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !formId) return;

    el.innerHTML = "";

    if (customStyles) {
      const style = document.createElement("style");
      style.textContent = customStyles;
      el.appendChild(style);
    }

    const frame = document.createElement("div");
    frame.className = "hs-form-frame";
    frame.setAttribute("data-region", HUBSPOT_REGION);
    frame.setAttribute("data-form-id", formId);
    frame.setAttribute("data-portal-id", HUBSPOT_PORTAL_ID);
    el.appendChild(frame);

    let cancelled = false;
    loadHubSpotFrameScript()
      .then(() => {
        if (cancelled) return;
        // The loader auto-scans the DOM for .hs-form-frame elements on load.
        // If it has already loaded once, trigger a rescan via the global API
        // (falls back silently if not exposed).
        const w = window as unknown as {
          HubSpotFormsV4?: { create?: (opts: Record<string, unknown>) => void };
          hbspt?: { forms?: { create?: (opts: Record<string, unknown>) => void } };
        };
        try {
          w.HubSpotFormsV4?.create?.({
            region: HUBSPOT_REGION,
            portalId: HUBSPOT_PORTAL_ID,
            formId,
            target: frame,
          });
        } catch {
          /* no-op: loader will pick it up via DOM scan */
        }
      })
      .catch((err) => {
        console.error("HubSpot frame embed failed to load", { formId, err });
      });

    return () => {
      cancelled = true;
      el.innerHTML = "";
    };
  }, [containerRef, formId, customStyles]);
}
